import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

export default function CreativeShowcase() {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [clicks, setClicks] = useState([]);
  const [isHovering, setIsHovering] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);

  // Interactive canvas for drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let particles = [];
    let animationId;

    class Particle {
      constructor(x, y, isClick = false) {
        this.x = x;
        this.y = y;
        this.size = isClick ? Math.random() * 8 + 4 : Math.random() * 3 + 1;
        this.speedX = (Math.random() - 0.5) * (isClick ? 8 : 2);
        this.speedY = (Math.random() - 0.5) * (isClick ? 8 : 2);
        this.life = isClick ? 60 : 30;
        this.maxLife = this.life;
        this.isClick = isClick;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.life--;
        if (this.isClick) {
          this.speedX *= 0.96;
          this.speedY *= 0.96;
        }
      }

      draw() {
        const alpha = this.life / this.maxLife;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * alpha, 0, Math.PI * 2);
        ctx.fillStyle = this.isClick 
          ? `rgba(212, 175, 55, ${alpha})` 
          : `rgba(212, 175, 55, ${alpha * 0.5})`;
        ctx.fill();
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles = particles.filter(p => p.life > 0);
      particles.forEach(p => {
        p.update();
        p.draw();
      });

      animationId = requestAnimationFrame(animate);
    };
    animate();

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Add trail particles on mouse move
      if (Math.random() > 0.5) {
        particles.push(new Particle(x, y, false));
      }
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Explosion of particles on click
      for (let i = 0; i < 20; i++) {
        particles.push(new Particle(x, y, true));
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Track mouse for 3D effect
  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      setMousePos({ x, y });
    }
  };

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className="relative min-h-screen bg-black overflow-hidden py-24 md:py-32 cursor-crosshair"
      data-testid="creative-showcase"
    >
      {/* Interactive Canvas Layer */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full z-10 pointer-events-auto"
        style={{ touchAction: 'none' }}
      />

      {/* Instruction hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovering ? 0 : 1 }}
        className="absolute top-8 left-1/2 -translate-x-1/2 z-20 text-xs text-zinc-500 tracking-widest uppercase"
      >
        Mueve el mouse y haz click para interactuar
      </motion.div>

      {/* Background shapes that follow mouse */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl"
        animate={{
          x: mousePos.x * 50,
          y: mousePos.y * 50,
          scale: isHovering ? 1.2 : 1,
        }}
        transition={{ type: 'spring', stiffness: 50 }}
      />
      <motion.div
        className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"
        animate={{
          x: mousePos.x * -30,
          y: mousePos.y * -30,
          scale: isHovering ? 1.3 : 1,
        }}
        transition={{ type: 'spring', stiffness: 30 }}
      />

      {/* Rotating ring that responds to mouse */}
      <motion.div
        style={{ rotate }}
        animate={{
          rotateX: mousePos.y * 20,
          rotateY: mousePos.x * 20,
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] md:w-[700px] md:h-[700px] pointer-events-none"
      >
        <svg viewBox="0 0 400 400" className="w-full h-full">
          <motion.circle
            cx="200" cy="200" r="180"
            fill="none" stroke="#D4AF37" strokeWidth="0.5" strokeDasharray="10 5"
            animate={{ strokeDashoffset: isHovering ? 100 : 0 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          />
          <circle cx="200" cy="200" r="150" fill="none" stroke="#D4AF37" strokeWidth="0.3" opacity="0.5" />
          <circle cx="200" cy="200" r="120" fill="none" stroke="#D4AF37" strokeWidth="0.2" opacity="0.3" />
        </svg>
      </motion.div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 pointer-events-none">
        <motion.div style={{ y, scale }} className="text-center">
          <motion.span
            className="inline-block text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37] mb-6"
            animate={{ 
              x: mousePos.x * 10,
              y: mousePos.y * 10,
            }}
          >
            Creative Vision
          </motion.span>

          <motion.h2
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.9] mb-6"
            animate={{
              rotateX: mousePos.y * 5,
              rotateY: mousePos.x * 5,
            }}
            style={{ transformPerspective: 1000 }}
          >
            <span className="text-white">Donde La</span>
            <br />
            <span className="gold-text">Creatividad</span>
            <br />
            <span className="text-white">Cobra Vida</span>
          </motion.h2>

          {/* Interactive stats */}
          <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto mb-16 pointer-events-auto">
            {[
              { number: '∞', label: 'Posibilidades' },
              { number: '100%', label: 'Compromiso' },
              { number: '24/7', label: 'Dedicación' },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                className="text-center cursor-pointer"
                whileHover={{ scale: 1.2, y: -10 }}
                whileTap={{ scale: 0.9 }}
                animate={{
                  x: mousePos.x * (10 + index * 5),
                  y: mousePos.y * (10 + index * 5),
                }}
              >
                <motion.div
                  className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2"
                  whileHover={{ 
                    textShadow: '0 0 30px rgba(212, 175, 55, 0.8)',
                  }}
                >
                  {item.number}
                </motion.div>
                <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">
                  {item.label}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Interactive morphing shape */}
          <motion.div
            className="relative w-48 h-48 mx-auto cursor-grab active:cursor-grabbing pointer-events-auto"
            drag
            dragConstraints={{ left: -100, right: 100, top: -50, bottom: 50 }}
            whileDrag={{ scale: 1.1 }}
            animate={{
              rotateX: mousePos.y * 30,
              rotateY: mousePos.x * 30,
            }}
            style={{ transformPerspective: 500 }}
          >
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FCF6BA" />
                  <stop offset="50%" stopColor="#D4AF37" />
                  <stop offset="100%" stopColor="#B38728" />
                </linearGradient>
              </defs>
              <motion.path
                d="M100,10 L120,80 L190,100 L120,120 L100,190 L80,120 L10,100 L80,80 Z"
                fill="none"
                stroke="url(#goldGradient)"
                strokeWidth="2"
                animate={{
                  d: isHovering
                    ? 'M100,20 L140,60 L180,100 L140,140 L100,180 L60,140 L20,100 L60,60 Z'
                    : 'M100,10 L120,80 L190,100 L120,120 L100,190 L80,120 L10,100 L80,80 Z',
                }}
                transition={{ duration: 0.5 }}
              />
            </svg>
            <motion.p className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs text-zinc-600 whitespace-nowrap">
              Arrastra la figura
            </motion.p>
          </motion.div>

          <motion.p
            className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mt-16 italic"
            animate={{ x: mousePos.x * 5, y: mousePos.y * 5 }}
          >
            "Transformamos ideas en experiencias visuales que conectan, inspiran y generan resultados."
          </motion.p>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent pointer-events-none" />
    </section>
  );
}
